<template>
  <v-row>
    <v-col>
      <v-card>
        <ValidationObserver ref="organizationForm" v-slot="{ invalid, validated }">
          <v-expansion-panels multiple>
            <v-expansion-panel v-test="{ panel: 'generalInfo' }">
              <ValidationObserver ref="info" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="info">
                    <v-icon>
                      info
                    </v-icon>
                    {{ $t('Общая информация') }}
                    <v-icon v-if="failed" color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="6"
                        class="text-left">
                      <validatable-text-input
                          v-test="{ input: 'organizationShortName' }"
                          :value="organizationInfo.organizationShortName"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Короткое наименование')"
                          @change="changeProperty({ organizationShortName: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'organizationFullName' }"
                          :value="organizationInfo.organizationFullName"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Полное наименование')"
                          @change="changeProperty({ organizationFullName: $event })" />
                      <validatable-identity-dictionary-selection
                          :value="organizationInfo.country"
                          rules="required"
                          readonly
                          :label="$t('Страна')"
                          prepend-icon="language"
                          :dictionary-type="DICTIONARY_TYPES.COUNTRIES"
                          @change="changeProperty({ country: $event })" />

                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-radio-group
                          v-test="{ radio: 'participantType' }"
                          :value="organizationInfo.participantType"
                          :items="participantTypes"
                          :items-tips="PARTICIPANT_TYPE_TIP_ENUM"
                          :readonly="readonly || readonlyParticipantTypes"
                          rules="required"
                          :label="$t('Категория участия')"
                          @change="changeParticipantType" />
                      <!-- <validatable-date-picker
                          :value="organizationInfo.registrationDate"
                          rules="required"
                          :readonly="readonly"
                          min-selected-date="1900-01-01"
                          :label="$t('Дата регистрации юридического лица')"
                          @change="changeProperty({ registrationDate: $event })" /> -->

                      <validatable-radio-group
                          v-test="{ radio: 'organizationType' }"
                          :value="organizationInfo.organizationType"
                          rules="required"
                          :items="ORGANIZATION_TYPE_ENUM"
                          :readonly="readonly"
                          :label="$t('Тип организации')"
                          @change="changeOrganizationType" />

                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3" />
                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>
            <v-expansion-panel v-if="isIndividual" v-test="{ panel: 'ipInfo' }">
              <ValidationObserver ref="person" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="person">
                    <v-icon>
                      person
                    </v-icon>
                    {{ $t('Документ, удостоверяющий личность физ. лица') }}
                    <v-icon
                        v-if="failed"
                        color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-enum-combobox
                          v-test="{ combobox: 'documentPersonIdentityType' }"
                          :value="organizationInfo.documentPersonIdentityType"
                          rules="required"
                          :values-enum="DOCUMENT_PERSON_IDENTITY_TYPES_ENUM"
                          clearable
                          :label="$t('Тип документа, удостоверяющего личность физ. лица')"
                          :loading="isLoading"
                          readonly
                          @change="changeProperty({ documentPersonIdentityType: $event })" />
                      <validatable-document-person-identity
                          v-test="{ input: 'documentPersonIdentity' }"
                          :value="organizationInfo.documentPersonIdentity"
                          :rules="{ required: true,
                                    regex: /^\d{4}\s{1}\d{6}$/ }"
                          :readonly="readonly"
                          @change="changeProperty({ documentPersonIdentity: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'documentPersonIdentityIssued' }"
                          :value="organizationInfo.documentPersonIdentityIssued"
                          rules="required"
                          :readonly="readonly"
                          :clearable="!readonly"
                          :label="$t('Кем и когда был выдан документ')"
                          @change="changeProperty({ documentPersonIdentityIssued: $event })" />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-date-picker
                          v-test="{ date: 'personBirthdate' }"
                          :value="organizationInfo.personBirthdate"
                          rules="required"
                          :readonly="readonly"
                          min-selected-date="1940-01-01"
                          :label="$t('Дата рождения физ. лица')"
                          @change="changeProperty({ personBirthdate: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'personRegistrationAddress' }"
                          :value="organizationInfo.personRegistrationAddress"
                          rules="required"
                          :readonly="readonly"
                          :label="$t('Адрес регистрации физ. лица')"
                          @change="changeProperty({ personRegistrationAddress: $event })" />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>

            <v-expansion-panel v-test="{ panel: 'eio' }">
              <ValidationObserver ref="eio" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="eio">
                    <v-icon>
                      person
                    </v-icon>
                    {{ $t('Единоличный исполнительный орган') }}
                    <v-icon v-if="failed" color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="12"
                        class="pl-3">
                      <validatable-checkbox
                          id="isEIO"
                          v-test="{ checkbox: 'isEIO' }"
                          :value="organizationInfo.isEIO"
                          rules=""
                          :readonly="readonly"
                          :label="$t('Регистрация от имени единоличного исполнительного органа')"
                          @change="changeProperty({ isEIO: $event })" />

                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'managerFIO' }"
                          :value="organizationInfo.managerFIO"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('ФИО')"
                          @change="changeProperty({ managerFIO: $event })" />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'managerPosition' }"
                          :value="organizationInfo.managerPosition"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Должность')"
                          @change="changeProperty({ managerPosition: $event })" />
                    </v-col>

                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>

            <v-expansion-panel v-test="{ panel: 'requsites' }">
              <ValidationObserver ref="requsites" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="requsites">
                    <v-icon>
                      business
                    </v-icon>
                    {{ $t('Реквизиты') }}
                    <v-icon
                        v-if="failed"
                        color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'inn' }"
                          :value="organizationInfo.inn"
                          :readonly="readonly"
                          :rules="{ required: isRussianCountryCode || !organizationInfo.ogrn,
                                    max: isRussianCountryCode?20:100,
                                    inn: isRussianCountryCode,
                                    numeric: isRussianCountryCode }"
                          :label="$t('ИНН')"
                          @change="changeProperty({ inn: $event })" />
                      <validatable-text-input
                          v-if="!isIndividual && isRussianCountryCode"
                          v-test="{ input: 'kpp' }"
                          :value="organizationInfo.kpp"
                          :rules="{ required: !isIndividual,
                                    max: 9,
                                    kpp: !isIndividual,
                                    numeric: !isIndividual }"
                          :readonly="readonly"
                          :label="$t('КПП')"
                          @change="changeProperty({ kpp: $event })" />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'ogrn' }"
                          :value="organizationInfo.ogrn"
                          :rules="{ required: isRussianCountryCode || !organizationInfo.inn,
                                    max: isRussianCountryCode?20:100,
                                    numeric: true,
                                    ogrn: !isIndividual,
                                    ogrnIp: isIndividual }"
                          :readonly="readonly"
                          :label="$t('ОГРН')"
                          @change="changeProperty({ ogrn: $event })" />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>

            <v-expansion-panel v-test="{ panel: 'contacts' }">
              <ValidationObserver ref="contacts" v-slot="{ failed }">
                <v-expansion-panel-header>
                  <div id="contacts">
                    <v-icon>
                      contact_mail
                    </v-icon>
                    {{ $t('Контактная информация') }}
                    <v-icon
                        v-if="failed"
                        color="error">
                      warning
                    </v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-row>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'postAddress' }"
                          :value="organizationInfo.postAddress"
                          rules="required"
                          :readonly="readonly"
                          prepend-icon="local_post_office"
                          :label="$t('Почтовый адрес')"
                          @change="changeProperty({ postAddress: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'realAddress' }"
                          :value="organizationInfo.realAddress"
                          rules="required"
                          :readonly="readonly"
                          prepend-icon="location_city"
                          :label="$t('Фактический адрес')"
                          @change="changeProperty({ realAddress: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'location' }"
                          :value="organizationInfo.location"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Юридический адрес')"
                          prepend-icon="location_on"
                          @change="changeProperty({ location: $event })" />

                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        class="pl-3">
                      <validatable-text-input
                          v-test="{ input: 'contactPerson' }"
                          :value="organizationInfo.contactPerson"
                          :readonly="readonly"
                          rules="required"
                          :label="$t('Контактное лицо (ФИО)')"
                          prepend-icon="person"
                          @change="changeProperty({ contactPerson: $event })" />
                      <validatable-text-input
                          v-test="{ input: 'phone' }"
                          :value="organizationInfo.phone"
                          :readonly="readonly"
                          :rules="{ required: true,
                                    max: 30,
                                    regex: /^[+]?[\d\s(\-);,]+$/ }"
                          :label="$t('Контактный телефон')"
                          prepend-icon="settings_phone"
                          :type="'tel'"
                          @change="changeProperty({ phone: $event })" />
                      <validatable-mail-list
                          v-test="{ 'mail-list': 'email' }"
                          :value="emailArray"
                          rules="required"
                          :label="$t('Адрес электронной почты')"
                          :readonly="readonly"
                          @change="changeEmail" />

                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </ValidationObserver>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-alert
              class="text-center"
              :value="invalid && validated"
              type="error">
            {{ $t('Не все поля заполнены.') }}
          </v-alert>
          <v-alert
              class="text-center"
              :value="!!countryError"
              type="error">
            {{ countryError }}
          </v-alert>
        </ValidationObserver>
      </v-card>
    </v-col>
  </v-row>

</template>

<script>
  import ValidatableRadioGroup from '@/components/reused/ui/ValidatableRadioGroup.vue'
  import ValidatableEnumCombobox from '@/components/reused/ui/ValidatableEnumCombobox.vue'
  import ValidatableDocumentPersonIdentity from '@/components/reused/ui/ValidatableDocumentPersonIdentity.vue'
  import ValidatableDatePicker from '@/components/reused/ui/ValidatableDatePicker.vue'
  import ValidatableMailList from '@/components/reused/ui/ValidatableMailList.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableIdentityDictionarySelection from '@/components/reused/ui/ValidatableIdentityDictionarySelection.vue'
  import ValidatableCheckbox from '@/components/reused/ui/ValidatableCheckbox.vue'

  import {
    PARTICIPANT_TYPE_TIP_ENUM,
    ORGANIZATION_TYPE_ENUM,
    DOCUMENT_PERSON_IDENTITY_TYPES_ENUM
  } from '@/constants/enums'
  import {
    ORGANIZATION_TYPES,
    DICTIONARY_TYPES,
    RUSSIAN_COUNTRY_ID,
    DOCUMENT_PERSON_IDENTITY_TYPES
  } from '@/constants/business'

  export default {
    components: {
      ValidatableRadioGroup,
      ValidatableEnumCombobox,
      ValidatableDatePicker,
      ValidatableMailList,
      ValidatableTextInput,
      ValidatableDocumentPersonIdentity,
      ValidatableIdentityDictionarySelection,
      ValidatableCheckbox
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      },
      organizationInfo: {
        type: Object,
        required: true
      },
      participantTypes: {
        type: Object,
        required: true
      },
      readonlyParticipantTypes: {
        type: Boolean
      }
    },
    data () {
      return {
        isLoading: false,
        DICTIONARY_TYPES,
        PARTICIPANT_TYPE_TIP_ENUM,
        ORGANIZATION_TYPE_ENUM,
        DOCUMENT_PERSON_IDENTITY_TYPES_ENUM
      }
    },
    computed: {
      isIndividual () {
        return this.organizationInfo.organizationType === ORGANIZATION_TYPES.INDIVIDUAL
      },
      isRussianCountryCode () {
        return this.organizationInfo.country === RUSSIAN_COUNTRY_ID
      },
      emailArray () {
        return this.organizationInfo?.email?.split(';') || []
      },
      countryError () {
        return !this.isRussianCountryCode && !this.readonly
          ? this.$t('Не резидентам РФ услуга по регистрации для участия в процедурах реализации не предоставляется')
          : null
      }
    },
    methods: {
      changeProperty: function (objWithEditedProps) {
        if (objWithEditedProps?.country &&
        objWithEditedProps?.country !== RUSSIAN_COUNTRY_ID) {
          objWithEditedProps.kpp = null
          objWithEditedProps.ogrn = null
        }
        this.$emit('change', objWithEditedProps)
      },
      changeParticipantType: function (participantType) {
        const objWithEditedProps = { participantType }

        this.changeProperty(objWithEditedProps)
      },
      changeOrganizationType: function (value) {
        const obj = {
          organizationType: value
        }

        if (value !== ORGANIZATION_TYPES.INDIVIDUAL) {
          obj.documentPersonIdentityType = null
          obj.personRegistrationAddress = null
          obj.personBirthdate = null
          obj.documentPersonIdentityIssued = null
          obj.documentPersonIdentity = null
          obj.kpp = null
        } else {
          obj.documentPersonIdentityType = DOCUMENT_PERSON_IDENTITY_TYPES.PASSPORT
        }

        this.changeProperty(obj)
      },
      changeEmail: function (value) {
        const email = Array.isArray(value) ? value.join(';') : `${value}`
        this.changeProperty({ email })
      },
      validate: async function () {
        const result = await this.$refs.organizationForm.validate()
        return !this.countryError && result
      }
    }
  }
</script>
